import { ColorsTypeEnum } from "~/utils/enums/colores.enums";
import {
  EvaluationTypeEnum,
  SessionSurveyOffsetAnchorEnum,
  SessionSurveyOffsetDirectionEnum,
  SessionSurveyOffsetUnitEnum,
  SessionSurveyParticipantStatusEnum,
  SessionSurveyRecipientEnum,
  SessionSurveySendModeEnum,
  SessionSurveyStatusEnum,
  SurveyParticipantSortKeyEnum,
} from "~/utils/enums/evaluations.enums";
import { EVALUATION_TYPE_LABEL } from "~/composables/evaluations/useEvaluationTemplates";
import type { ISessionSurvey, ISessionSurveyParticipant } from "~/utils/interfaces/evaluations-interfaces";
import type { IIndexHeader } from "~/utils/interfaces/table-interfaces";

interface ITag {
  value: string;
  type: ColorsTypeEnum;
}

const SURVEY_STATUS_TAG: Record<SessionSurveyStatusEnum, ITag> = {
  [SessionSurveyStatusEnum.SCHEDULED]: { value: "Programmée", type: ColorsTypeEnum.LIGHT_PURPLE },
  [SessionSurveyStatusEnum.SENT]: { value: "Envoyée", type: ColorsTypeEnum.LIGHT_ORANGE },
};

const PARTICIPANT_STATUS_TAG: Record<SessionSurveyParticipantStatusEnum, ITag> = {
  [SessionSurveyParticipantStatusEnum.SCHEDULED]: { value: "Programmé", type: ColorsTypeEnum.LIGHT_PURPLE },
  [SessionSurveyParticipantStatusEnum.SENT]: { value: "Envoyé", type: ColorsTypeEnum.LIGHT_ORANGE },
  [SessionSurveyParticipantStatusEnum.COMPLETED]: { value: "Complété", type: ColorsTypeEnum.LIGHT_GREEN },
  [SessionSurveyParticipantStatusEnum.PENDING]: { value: "En attente", type: ColorsTypeEnum.LIGHT_GREY },
};

const RECIPIENT_LABEL: Record<SessionSurveyRecipientEnum, string> = {
  [SessionSurveyRecipientEnum.PARTICIPANTS]: "Participants",
  [SessionSurveyRecipientEnum.MANAGERS]: "Managers",
};

const SEND_MODE_LABEL: Record<SessionSurveySendModeEnum, string> = {
  [SessionSurveySendModeEnum.NOW]: "Envoyer maintenant",
  [SessionSurveySendModeEnum.SCHEDULED]: "Programmer l'envoi",
};

const OFFSET_UNIT_LABEL: Record<SessionSurveyOffsetUnitEnum, string> = {
  [SessionSurveyOffsetUnitEnum.HOUR]: "heure(s)",
  [SessionSurveyOffsetUnitEnum.DAY]: "jour(s)",
};

const OFFSET_DIRECTION_LABEL: Record<SessionSurveyOffsetDirectionEnum, string> = {
  [SessionSurveyOffsetDirectionEnum.BEFORE]: "avant",
  [SessionSurveyOffsetDirectionEnum.AFTER]: "après",
};

const OFFSET_ANCHOR_LABEL: Record<SessionSurveyOffsetAnchorEnum, string> = {
  [SessionSurveyOffsetAnchorEnum.SESSION_START]: "le début de la session",
  [SessionSurveyOffsetAnchorEnum.SESSION_END]: "la fin de la session",
};

// PRD AC-SESSEVAL-06.1 column labels; Statut is sortable (US-SESSEVAL-06 "tri par statut").
// "Participant" is always the LEARNER. An à-froid enquête is answered by that learner's manager, so it
// gets an extra "Répondant" column right after (the manager IS the respondent) — that name used to be
// shown under "Participant", which read as the wrong person. À chaud the learner answers under
// "Participant", so there is no separate respondent column: it is omitted rather than left empty.
const PARTICIPANT_HEADERS: IIndexHeader[] = [
  { name: "Participant", key: SurveyParticipantSortKeyEnum.PARTICIPANT, minWidthPx: 260, visible: true },
  { name: "Statut", key: SurveyParticipantSortKeyEnum.STATUS, sort: true, minWidthPx: 130, visible: true },
  { name: "Date d'envoi", minWidthPx: 170, visible: true },
  { name: "Date réponse", minWidthPx: 150, visible: true },
  { name: "Note globale", minWidthPx: 120, visible: true },
];

// Only ever injected for à froid (see participantHeaders), where the manager answers the enquête — so it is
// labelled "Répondant", matching the global /evaluations à froid tab. No per-tab logic needed here.
const MANAGER_HEADER: IIndexHeader = { name: "Répondant", minWidthPx: 220, visible: true };

// A questionnaire is never sent: its step's date stands in for the send date, and its score in % for the note.
const QUESTIONNAIRE_HEADERS: IIndexHeader[] = [
  PARTICIPANT_HEADERS[0]!,
  PARTICIPANT_HEADERS[1]!,
  { name: "Date", minWidthPx: 170, visible: true },
  PARTICIPANT_HEADERS[3]!,
  { name: "Score", key: SurveyParticipantSortKeyEnum.SCORE, sort: true, minWidthPx: 120, visible: true },
];

// Lifecycle order, not alphabetical: an enquête is scheduled then sent, a questionnaire awaits its answer.
const STATUS_RANK: Record<SessionSurveyParticipantStatusEnum, number> = {
  [SessionSurveyParticipantStatusEnum.SCHEDULED]: 0,
  [SessionSurveyParticipantStatusEnum.SENT]: 1,
  [SessionSurveyParticipantStatusEnum.PENDING]: 1,
  [SessionSurveyParticipantStatusEnum.COMPLETED]: 2,
};

const SORTERS: Record<
  SurveyParticipantSortKeyEnum,
  (a: ISessionSurveyParticipant, b: ISessionSurveyParticipant) => number
> = {
  [SurveyParticipantSortKeyEnum.PARTICIPANT]: (a, b) => a.attendeeName.localeCompare(b.attendeeName),
  [SurveyParticipantSortKeyEnum.STATUS]: (a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status],
  [SurveyParticipantSortKeyEnum.SCORE]: (a, b) => (a.score ?? -1) - (b.score ?? -1),
};

export function useSessionEvaluations() {
  const surveyStatusTag = (status: SessionSurveyStatusEnum): ITag => SURVEY_STATUS_TAG[status];
  const participantStatusTag = (status: SessionSurveyParticipantStatusEnum): ITag => PARTICIPANT_STATUS_TAG[status];
  const recipientTag = (recipient: SessionSurveyRecipientEnum): ITag => ({
    value: RECIPIENT_LABEL[recipient],
    type: ColorsTypeEnum.LIGHT_GREY,
  });
  const typeLabel = (type: EvaluationTypeEnum): string => EVALUATION_TYPE_LABEL[type];
  const sendModeLabel = (mode: SessionSurveySendModeEnum): string => SEND_MODE_LABEL[mode];
  const offsetUnitLabel = (unit: SessionSurveyOffsetUnitEnum): string => OFFSET_UNIT_LABEL[unit];
  const offsetDirectionLabel = (dir: SessionSurveyOffsetDirectionEnum): string => OFFSET_DIRECTION_LABEL[dir];
  const offsetAnchorLabel = (anchor: SessionSurveyOffsetAnchorEnum): string => OFFSET_ANCHOR_LABEL[anchor];

  // "2026-06-05" → "05/06/2026"; null/empty → "—". A questionnaire's answer comes timestamped ("2026-06-05 14:02:…").
  const formatDate = (iso?: string | null): string => {
    if (!iso) return "—";
    const [y, m, d] = iso.slice(0, 10).split("-");
    return y && m && d ? `${d}/${m}/${y}` : "—";
  };

  const pendingCount = (survey: ISessionSurvey): number =>
    survey.participants.filter((p) => p.status !== SessionSurveyParticipantStatusEnum.COMPLETED).length;

  /** Ties keep the alphabetical order, so a sorted column still reads in name order inside each group. */
  function sortParticipants(
    participants: ISessionSurveyParticipant[],
    key: SurveyParticipantSortKeyEnum,
    descending: boolean,
  ): ISessionSurveyParticipant[] {
    const byName = SORTERS[SurveyParticipantSortKeyEnum.PARTICIPANT];
    const direction = descending ? -1 : 1;
    return [...participants].sort((a, b) => SORTERS[key](a, b) * direction || byName(a, b));
  }

  return {
    surveyStatusTag,
    participantStatusTag,
    recipientTag,
    typeLabel,
    sendModeLabel,
    offsetUnitLabel,
    offsetDirectionLabel,
    offsetAnchorLabel,
    // Type-aware so the à-froid table gains the "Répondant" column. Insert after "Participant" (index 1).
    participantHeaders: (type: EvaluationTypeEnum): IIndexHeader[] => {
      if (type === EvaluationTypeEnum.QUESTIONNAIRE) return QUESTIONNAIRE_HEADERS;
      return type === EvaluationTypeEnum.DELAYED
        ? [PARTICIPANT_HEADERS[0]!, MANAGER_HEADER, ...PARTICIPANT_HEADERS.slice(1)]
        : PARTICIPANT_HEADERS;
    },
    sortParticipants,
    formatDate,
    pendingCount,
  };
}
